<template>
  <div class="customer-contracts-list-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>销售合同</span>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.contract_name"
        placeholder="合同名称"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-input
        v-model="searchParams.supplierName"
        placeholder="供应商名称"
        clearable
        class="search-input"
        prefix-icon="el-icon-s-custom"
      ></el-input>
      
      <el-select
        v-model="searchParams.status"
        placeholder="状态"
        clearable
        class="search-input"
      >
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
      
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button v-permission="'contracts:customer-contracts-list:create'" type="success" @click="createContract">新增销售合同</el-button>
    </div>
    
    <!-- 合同列表 -->
    <el-table
      :data="contractList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column
        prop="contract_name"
        label="合同名称"
        min-width="280"
      ></el-table-column>
      
      <el-table-column
        label="客户信息"
        min-width="300"
        align="left"
      >
        <template slot-scope="scope">
          <!-- 使用新API返回的客户名称 -->
          <div v-if="scope.row.partner_name || scope.row.customer_name" class="customer-info-container">
            <div class="customer-project-item">
              <el-tooltip 
                effect="dark" 
                placement="top"
                :content="scope.row.partner_name || scope.row.customer_name"
              >
                <div class="customer-project-text">
                  {{ scope.row.partner_name || scope.row.customer_name }}
                </div>
              </el-tooltip>
            </div>
          </div>
          <!-- 兼容旧的数据结构 -->
          <div v-else-if="scope.row.customers_list_contract && scope.row.customers_list_contract.length > 0" class="customer-info-container">
            <div v-for="(cp, index) in scope.row.customers_list_contract" :key="index" class="customer-project-item">
              <el-tooltip 
                effect="dark" 
                placement="top"
                :content="`${cp.unit_info?.unit_name || ''}`"
              >
                <div class="customer-project-text">
                  {{ cp.unit_info?.unit_name || '' }}
                </div>
              </el-tooltip>
            </div>
          </div>
          <span v-else>无客户信息</span>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="status"
        label="状态"
        width="150"
        align="center"
      >
        <template slot-scope="scope">
          <el-tag :type="getStatusTagType(scope.row.status)">
            {{ scope.row.status | statusFilter }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="create_time"
        label="创建时间"
        width="180"
        align="center"
      ></el-table-column>
      
      <el-table-column
        label="操作"
        width="220"
        align="center"
      >
        <template slot-scope="scope">
          <div>
            <el-button
              type="primary"
              size="mini"
              @click="viewContract(scope.row.id)"
            >查看</el-button>
            
            <el-button
              v-if="isCreatedByCurrentUser(scope.row) && isEditableStatus(scope.row)"
              v-permission="'contracts:customer-contracts-list:edit'"
              type="warning"
              size="mini"
              @click="editContract(scope.row.id)"
            >编辑</el-button>
            
            <el-button
              v-if="isCreatedByCurrentUser(scope.row) && isEditableStatus(scope.row)"
              v-permission="'contracts:customer-contracts-list:delete'"
              type="danger"
              size="mini"
              @click="deleteContract(scope.row.id)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { getUserInfo } from '@/utils/auth.js';
import api from '@/api';
import moment from 'moment';

export default {
  name: 'CustomerContractsList',
  filters: {
    statusFilter(status) {
      const statusMap = {
        'DRAFT': '草稿',
        'ACTIVE': '签订',
        'EXECUTING': '执行中',
        'SETTLED': '决算',
        'FINISHED': '收尾',
        'TERMINATED': '终止'
      };
      return statusMap[status] || status;
    }
  },
  data() {
    return {
      searchParams: {
        contract_name: '',
        supplierName: '',
        status: '',
        create_id: '' // 当前用户单位ID
      },
      statusOptions: [
        { value: 'DRAFT', label: '草稿' },
        { value: 'ACTIVE', label: '签订' },
        { value: 'EXECUTING', label: '执行中' },
        { value: 'SETTLED', label: '决算' },
        { value: 'FINISHED', label: '收尾' },
        { value: 'TERMINATED', label: '终止' }
      ],
      loading: false,
      contractList: [],
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      originalResponse: null, // 存储原始响应数据
      currentUser: null,
      customersAndProjects: [], // 存储客户和项目信息
      customerMap: {}, // 客户ID到名称的映射
      projectMap: {} // 项目ID到名称的映射
    };
  },
  created() {
    // 获取当前用户信息
    this.currentUser = getUserInfo();
// 设置当前用户单位ID作为筛选条件
    if (this.currentUser && this.currentUser.unit_id) {
      this.searchParams.create_id = this.currentUser.unit_id;
} else if (this.currentUser && this.currentUser.unitId) {
      // 兼容不同属性名
      this.searchParams.create_id = this.currentUser.unitId;
} else {
      console.warn('未能获取用户单位ID');
      // 尝试从localStorage获取
      try {
        const localUserInfo = localStorage.getItem('userInfo');
        if (localUserInfo) {
          const userInfo = JSON.parse(localUserInfo);
          if (userInfo.unit_id || userInfo.unitId) {
            this.searchParams.create_id = userInfo.unit_id || userInfo.unitId;
}
        }
      } catch (e) {
        console.error('从localStorage获取用户信息失败:', e);
      }
    }
    
    // 获取合同列表
    this.getContractList();
  },
  methods: {
    // 获取合同列表
    getContractList() {
      this.loading = true;
      
      // 获取当前用户单位ID
      const unitId = this.currentUser?.unit_id || this.currentUser?.unitId;
      if (!unitId) {
        this.$message.error('无法获取当前用户单位信息，请重新登录');
        this.loading = false;
        return;
      }
      
      // 构建查询参数 - 使用新的销售合同专用API
      const params = {
        page: this.pagination.currentPage - 1, // 后端分页从0开始
        size: this.pagination.pageSize,
        sortBy: 'id',
        sortDir: 'desc'
      };
      
      // 添加搜索条件
      if (this.searchParams.contract_name) {
        params.contractName = this.searchParams.contract_name;
      }
      if (this.searchParams.status) {
        // 映射前端状态到后端状态
        const statusMap = {
          'DRAFT': '草稿',
          'ACTIVE': '签订', 
          'EXECUTING': '执行中',
          'SETTLED': '决算',
          'FINISHED': '收尾',
          'TERMINATED': '终止'
        };
        params.status = statusMap[this.searchParams.status] || this.searchParams.status;
      }
      
// 调用新的销售合同查询API
      api.contracts.query_sales_contracts_pagination(unitId, params)
        .then(response => {
this.originalResponse = response.data; // 保存原始响应
          
          if (response.data && response.data.success) {
            // 使用新的销售合同API数据结构 response.data.data.content
            if (response.data.data && response.data.data.content) {
              this.contractList = response.data.data.content || [];
              this.pagination.total = response.data.data.totalElements || 0;
            } else {
              this.contractList = [];
              this.pagination.total = 0;
            }
            
            // 处理合同数据，适配新的API格式
            this.contractList = this.contractList.map(item => {
              // 映射新API字段到前端期望的字段
              const processedItem = {
                id: item.id,
                contract_name: item.contractName,
                status: item.status,
                create_time: item.createTime ? this.formatDate(item.createTime) : '',
                create_id: item.creatorId,
                // 从contractParties中提取客户信息
                customer_name: this.extractCustomerName(item.contractParties),
                // 保留原始数据
                ...item
              };
              
              return processedItem;
            });
            
} else {
            this.$message.error(response.data.message || '获取销售合同列表失败');
            this.contractList = [];
            this.pagination.total = 0;
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取销售合同列表失败:', error);
          this.$message.error('获取销售合同列表失败: ' + (error.message || '未知错误'));
          this.contractList = [];
          this.pagination.total = 0;
          this.loading = false;
        });
    },
    
    // 搜索
    handleSearch() {
this.pagination.currentPage = 1;
      this.getContractList();
    },
    
    // 页码改变
    handleCurrentChange(currentPage) {
      this.pagination.currentPage = currentPage;
      this.getContractList();
    },
    
    // 每页数量改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.currentPage = 1;
      this.getContractList();
    },
    
    // 获取状态对应的标签类型
    getStatusTagType(status) {
      const statusMap = {
        'DRAFT': 'info',
        'ACTIVE': 'primary', 
        'EXECUTING': 'success',
        'SETTLED': 'warning',
        'FINISHED': 'info',
        'TERMINATED': 'danger'
      };
      return statusMap[status] || 'info';
    },
    
    // 编辑合同
    editContract(id) {
      this.$router.push(`/contracts/customer-contract-edit/${id}`);
    },
    
    // 删除合同
    deleteContract(id) {
      this.$confirm('确认删除该合同?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 调用删除API
        api.contracts.del_contract_by_id(id)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('删除成功');
              this.getContractList(); // 刷新列表
            } else {
              this.$message.error(response.data.message || '删除失败');
            }
          })
          .catch(error => {
            console.error('删除合同失败:', error);
            this.$message.error('删除合同失败');
          });
      }).catch(() => {
        // 取消删除
      });
    },
    
    // 查看合同
    viewContract(id) {
      // 查找当前合同对象
      const contract = this.contractList.find(item => item.id === id);
      
      if (contract) {
// 获取用户ID和合同创建者ID
        const currentUnitId = this.currentUser?.unit_id || this.currentUser?.unitId;
        const creatorId = contract.create_id;
        
// 将ID转换为字符串进行比较
        const isCreator = String(currentUnitId) === String(creatorId);
// 根据是否为创建者决定跳转页面
        if (isCreator) {
          // 如果是当前用户创建的合同，跳转到preview页面
// 根据contract_type判断跳转不同的合同预览页面
          if (contract.contract_type === 'supplier') {
            // 供应商合同类型
            this.$router.push(`/contracts/supplier/preview/${id}`);
          } else {
            // 客户合同类型（默认）
            this.$router.push(`/contracts/customer/view/${id}`);
          }
        } else {
          // 如果不是当前用户创建的合同，跳转到confirm页面
// 根据contract_type判断跳转不同的合同确认页面
          if (contract.contract_type === 'supplier') {
            // 供应商合同类型
            this.$router.push(`/contracts/supplier-contract-confirm/${id}`);
          } else {
            // 客户合同类型（默认）
            this.$router.push(`/contracts/customer-contract-confirm/${id}`);
          }
        }
      } else {
        // 找不到合同对象，使用默认跳转
        console.warn('未找到合同信息，使用默认跳转到confirm页面');
        this.$router.push(`/contracts/customer-contract-confirm/${id}`);
      }
    },
    
    // 新增合同
    createContract() {
      this.$router.push(`/contracts/customer-contract-edit`);
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        return moment(dateString).format('YYYY-MM-DD');
      } catch (error) {
        console.error('日期格式化错误:', error);
        return dateString;
      }
    },
    
    // 获取客户名称
    getCustomerName(customer_id) {
      // 如果ID为空，返回默认值
      if (!customer_id) return '未知客户';
      
      // 首先尝试从映射中获取
      if (this.customerMap[customer_id]) {
        return this.customerMap[customer_id];
      }
      
      // 如果映射中没有，尝试从合同列表获取
      for (const contract of this.contractList) {
        if (contract.customerProjects && contract.customerProjects.length > 0) {
          for (const cp of contract.customerProjects) {
            // 检查customer_id是否匹配
            if (cp.customer_id === customer_id) {
              // 检查unit_info对象 (新的API返回结构)
              if (cp.unit_info) {
                if (cp.unit_info.unit_name) return cp.unit_info.unit_name;
                if (cp.unit_info.name) return cp.unit_info.name;
              }
              
              // 检查Customer对象 (旧的API返回结构)
              if (cp.Customer) {
                if (cp.Customer.unit_name) return cp.Customer.unit_name;
                if (cp.Customer.name) return cp.Customer.name;
                if (cp.Customer.customer_name) return cp.Customer.customer_name;
              }
              
              // 如果customerProject对象上有客户名称属性
              if (cp.customer_name) return cp.customer_name;
              if (cp.unit_name) return cp.unit_name;
            }
          }
        }
      }
      
      // 如果在现有数据中找不到客户名称，返回ID
      return `客户(ID:${customer_id})`;
    },
    
    // 获取项目名称
    getProjectName(project_id) {
      // 如果ID为空，返回默认值
      if (!project_id) return '';
      
      // 首先尝试从映射中获取
      if (this.projectMap[project_id]) {
        return this.projectMap[project_id];
      }
      
      // 如果映射中没有，尝试从合同列表获取
      for (const contract of this.contractList) {
        if (contract.customerProjects && contract.customerProjects.length > 0) {
          for (const cp of contract.customerProjects) {
            // 检查project_id是否匹配
            if (cp.project_id === project_id) {
              // 检查Project对象 (旧的API返回结构)
              if (cp.Project) {
                if (cp.Project.project_name) return cp.Project.project_name;
                if (cp.Project.name) return cp.Project.name;
              }
              
              // 如果项目信息在project对象中 (新的API可能的结构)
              if (cp.project) {
                if (cp.project.project_name) return cp.project.project_name;
                if (cp.project.name) return cp.project.name;
              }
              
              // 如果customerProject对象上有项目名称属性
              if (cp.project_name) return cp.project_name;
            }
          }
        }
      }
      
      // 如果在现有数据中找不到项目名称，返回ID
      return `项目(ID:${project_id})`;
    },
    
    // 从contractParties中提取客户名称
    extractCustomerName(contractParties) {
      if (!contractParties || !Array.isArray(contractParties)) {
        return '无客户信息';
      }
      
      // 查找客户类型的参与方
      const customer = contractParties.find(party => party.partyType === 'CUSTOMER');
      if (customer && customer.unitInfo) {
        return customer.unitInfo.unitName || customer.unitInfo.name || '未知客户';
      }
      
      // 如果没有找到客户，返回第一个参与方的名称
      if (contractParties.length > 0 && contractParties[0].unitInfo) {
        return contractParties[0].unitInfo.unitName || contractParties[0].unitInfo.name || '未知客户';
      }
      
      return '无客户信息';
    },
    
    // 判断是否为可编辑状态
    isEditableStatus(row) {
      // 只有草稿和签订状态的合同可以编辑
      return row.status === 'DRAFT' || row.status === 'ACTIVE';
    },
    
    // 判断是否为当前用户创建的合同
    isCreatedByCurrentUser(row) {
      // 获取当前用户ID
      const currentUserId = this.currentUser?.id || this.currentUser?.userId;
      return String(currentUserId) === String(row.creatorId || row.create_id);
    }
  }
};
</script>

<style lang="scss" scoped>
.customer-contracts-list-container {
  background-color: #fff;
  padding: 20px;
  
  .page-header {
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      font-size: 18px;
      font-weight: bold;
      
      .title-mark {
        width: 4px;
        height: 20px;
        background-color: #409EFF;
        margin-right: 10px;
      }

      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .search-area {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    
    .search-input {
      width: 220px;
      margin-right: 15px;
      margin-bottom: 10px;
    }
    
    .el-button {
      margin-bottom: 10px;
    }
  }
  
  .customer-info-container {
    padding: 4px 0;
  }
  
  .customer-project-item {
    padding: 4px 8px;
    margin-bottom: 4px;
    border-radius: 4px;
    background-color: #f5f7fa;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      background-color: #ecf5ff;
    }
  }
  
  .customer-project-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #606266;
    font-size: 13px;
    line-height: 1.4;
  }
  
  .material-card {
    margin: 10px 20px;
    
    .el-card__header {
      padding: 10px 15px;
      background-color: #f5f7fa;
      font-weight: bold;
    }
    
    .el-table {
      margin: 10px 0;
    }
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>